import React, {useState} from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Divider,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  AccessTime, 
  CheckCircle,
  AutoAwesomeMotion,
  MenuBook,
  AccountTree,
  SwapHoriz,
  Warning
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { learningPathService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import moment from 'moment';
import { useEmbedded } from '../../hooks/useEmbedded'
import { tokens } from '../../theme';
import TypeTag from '../shared/TypeTag';

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: '8px',
  border: '1px solid #bbb'
}));

const DetailItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  }
}));

const RequiredModuleItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  border: `1px solid ${theme.palette.warning.main}`,
  backgroundColor: 'rgba(255, 152, 0, 0.04)',
  borderRadius: '4px',
  marginBottom: theme.spacing(0.5),
}));

const LearningPathDetails = ({ 
  lpath, 
  instance, 
  instances, 
  contentStats,
  progress,
  enrolled,
  enrolledObj,
  prerequisites,
  showEnrollCount,
  enrollmentCount,
  firstIncompleteContent,
  refreshOnComplete,
  refreshOnSwitch = null,
  contents
}) => {
 
  const { showToast } = useToast();
  const isEmbedded = useEmbedded();
  //const isEnrolledInCurrentInstance = enrolledObj && enrolledObj.lpath_instance_id === instance?.id;
  const isEnrolledInCurrentInstance = enrolledObj;
  
  const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('MMM DD, YYYY') : 'Not specified';
  };

  const requiredModules = contents ? contents.filter(content => content.required === "1") : [];
  const hasRequiredModules = requiredModules.length > 0;
  
  return (
    <Box>
      <SectionPaper elevation={0}>
        {isEnrolledInCurrentInstance && progress && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: tokens.color.textSecondary,
                mb: 1,
              }}
            >
              Your progress
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.color.textPrimary }}>
                {Math.round(progress.progress)}%
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.color.textSecondary }}>
                {contentStats.completed} of {contentStats.total} items
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={progress.progress}
              sx={{
                height: 8,
                borderRadius: 3,
                backgroundColor: tokens.color.bgTertiary,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: progress.progress >= 100 ? tokens.color.positive : tokens.color.brand,
                },
              }}
            />
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}
        
        {lpath.duration && (
          <DetailItem>
            <AccessTime fontSize="small" />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant="body2" sx={{ color: tokens.color.textSecondary }}>
                Duration
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.textPrimary }}>
                {lpath.duration}
              </Typography>
            </Box>
          </DetailItem>
        )}

        {contentStats.courses > 0 && (
          <DetailItem>
            <TypeTag type="course" compact />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', ml: 1 }}>
              <Typography variant="body2" sx={{ color: tokens.color.textSecondary }}>
                Courses
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.textPrimary }}>
                {contentStats.courses}
              </Typography>
            </Box>
          </DetailItem>
        )}

        {contentStats.learningPaths > 0 && (
          <DetailItem>            
            <TypeTag type="learning path" compact />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', ml: 1 }}>
              <Typography variant="body2" sx={{ color: tokens.color.textSecondary }}>
                Learning Paths
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.textPrimary }}>
                {contentStats.learningPaths}
              </Typography>
            </Box>
          </DetailItem>
        )}
        {/* Instance Section */}
        {/* {instance && (
          <DetailItem>
            <AutoAwesomeMotion fontSize="small" />
            <Box>            
              <Typography variant="body2" color="textSecondary">
                Current Instance
              </Typography>
              <Typography variant="body1">
                {instance.title || `Instance ${instance.id}`}
                {instance.default_instance === '1' && (
                  <Chip 
                    label="Default" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography> */}
              {/* Add "View All" link if there are multiple instances */}
              {/* {instances && instances.length > 1 && (
                <Link 
                  to={`${isEmbedded ? '/embed' : ''}/learner/lpath/${lpath.id}/instances`} 
                  state={{ 
                    instances, 
                    lpath, 
                    enrolledInstanceId: enrolledObj?.lpath_instance_id,
                    progress,
                    lpathId: lpath.id
                  }}
                  style={{ textDecoration: 'none' }}
                >
                  <Typography variant="body2" color="primary">
                    View All
                  </Typography>
                </Link>
              )}
            </Box>
          </DetailItem>
        )} 
        
        {instance && instance.completion_deadline && (
          <DetailItem>
            <AccessTime fontSize="small" />
            <Box>
              <Typography variant="body2" color="textSecondary">
                Completion Deadline
              </Typography>
              <Typography variant="body1">
                {formatDate(instance.completion_deadline)}
              </Typography>
            </Box>
          </DetailItem>
        )}
        */}
        <Divider sx={{ my: 2 }} />                                
        {lpath.required_count > 0 && lpath.required_count < contentStats.total ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              
              <Typography variant="body2">
                Complete at least <strong>{lpath.required_count}</strong> of {contentStats.total} modules to finish this learning path.
              </Typography>
            </Box>
            
            {/* Display required modules if available */}
            {hasRequiredModules && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Required modules:
                </Typography>
                <List dense disablePadding>
                  {requiredModules.map((module, index) => (
                    <RequiredModuleItem key={index}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <Warning fontSize="small" color="warning" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={module.name} 
                        primaryTypographyProps={{ 
                          variant: 'body2',
                          fontWeight: 500
                        }}
                        secondary={`${module.type.charAt(0).toUpperCase() + module.type.slice(1)}`}
                      />
                      {module.progress && module.progress.status === "1" && (
                        <CheckCircle fontSize="small" color="success" />
                      )}
                    </RequiredModuleItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant="body2">
            Complete all modules to finish this learning path.
          </Typography>
        )}


        {prerequisites && prerequisites.prerequisites && prerequisites.prerequisites.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Prerequisites Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">
                {prerequisites.completed} of {prerequisites.total} prerequisites completed
                {prerequisites.required > 0 && ` (${prerequisites.required} required)`}
              </Typography>
            </Box>
            {prerequisites.required > prerequisites.completed && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Warning fontSize="small" color="primary" sx={{ mr: 0.8 }} />
                <Typography variant="body2" color="greyShade.main">
                  You must complete all required prerequisites before starting this learning path.
                </Typography>
              </Box>
            )}
          </>
        )}
      </SectionPaper>
    </Box>
  );
};

export default LearningPathDetails;